<template>
  <div class="settings-section">
    <h2 class="section-title">Unit Types</h2>
    <p class="section-description">Choose which units are available when creating products. Only enabled units appear in the unit dropdown.</p>

    <div v-if="isLoading" class="loading-state">Loading unit settings...</div>

    <template v-else>
      <div class="settings-form">
        <!-- Default unit -->
        <div class="form-field">
          <IntercomSelect
            v-model="formData.default_unit_code"
            label="Default Unit"
            :options="enabledOptions"
            placeholder="Select default unit..."
            hint="Pre-selected when creating a new product"
          />
        </div>

        <!-- Unit groups (checkboxes) -->
        <div class="form-field">
          <label class="form-label">Enabled Units</label>
          <p class="field-hint">Check the units your business uses. At least one unit must remain enabled.</p>

          <div v-if="errorMsg" class="error-banner">{{ errorMsg }}</div>

          <div class="unit-groups">
            <div
              v-for="group in UNIT_CODE_GROUPS"
              :key="group.label"
              class="unit-group"
            >
              <div class="unit-group-header">{{ group.label }}</div>
              <div class="unit-checkboxes">
                <label
                  v-for="code in group.codes"
                  :key="code"
                  class="unit-checkbox-label"
                >
                  <input
                    type="checkbox"
                    class="unit-checkbox"
                    :value="code"
                    :checked="formData.enabled_unit_codes.includes(code)"
                    @change="toggleUnit(code)"
                  />
                  <span class="unit-code">{{ code }}</span>
                  <span class="unit-name">{{ getUnitName(code) }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Save Footer -->
      <div class="save-footer" v-if="isDirty || saveStatus">
        <button
          v-if="isDirty"
          class="btn-discard"
          @click="handleDiscard"
        >
          Discard
        </button>
        <IntercomButton
          variant="primary"
          :loading="isSaving"
          :disabled="!isDirty && saveStatus !== 'saved'"
          @click="handleSave"
        >
          {{ saveStatus === 'saved' ? 'Saved' : 'Save Changes' }}
        </IntercomButton>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useToast } from '@shared/composables/useToast.js'
import { IntercomButton, IntercomSelect } from '@shared/components/intercom'
import { useBilling } from '../../composables/useBilling.js'
import { MASTER_UNIT_CODES, UNIT_CODE_GROUPS, buildUnitOptions, getUnitName } from '../../constants/unitTypes.js'

const toast = useToast()
const { getBillingSettings, updateBillingSettings } = useBilling()

const isLoading = ref(true)
const isSaving = ref(false)
const saveStatus = ref(null)
const errorMsg = ref('')

const formData = reactive({
  enabled_unit_codes: [],
  default_unit_code: 'PCS',
})

const originalData = ref({
  enabled_unit_codes: [],
  default_unit_code: 'PCS',
})

// Options for the default-unit select — only enabled units
const enabledOptions = computed(() => buildUnitOptions(formData.enabled_unit_codes))

const isDirty = computed(() => {
  const orig = originalData.value
  if (formData.default_unit_code !== orig.default_unit_code) return true
  if (formData.enabled_unit_codes.length !== orig.enabled_unit_codes.length) return true
  const origSet = new Set(orig.enabled_unit_codes)
  return formData.enabled_unit_codes.some((c) => !origSet.has(c))
})

const toggleUnit = (code) => {
  errorMsg.value = ''
  const idx = formData.enabled_unit_codes.indexOf(code)
  if (idx >= 0) {
    // Disabling — ensure at least one remains
    if (formData.enabled_unit_codes.length <= 1) {
      errorMsg.value = 'At least one unit must remain enabled.'
      return
    }
    formData.enabled_unit_codes.splice(idx, 1)
    // If default unit was just disabled, reset to first available
    if (formData.default_unit_code === code && formData.enabled_unit_codes.length > 0) {
      formData.default_unit_code = formData.enabled_unit_codes[0]
    }
  } else {
    formData.enabled_unit_codes.push(code)
  }
}

const handleSave = async () => {
  if (!isDirty.value) return
  if (formData.enabled_unit_codes.length === 0) {
    errorMsg.value = 'At least one unit must remain enabled.'
    return
  }

  isSaving.value = true
  errorMsg.value = ''
  try {
    await updateBillingSettings({
      enabled_unit_codes: formData.enabled_unit_codes,
      default_unit_code: formData.default_unit_code,
    })
    originalData.value = {
      enabled_unit_codes: [...formData.enabled_unit_codes],
      default_unit_code: formData.default_unit_code,
    }
    saveStatus.value = 'saved'
    toast.success('Unit settings saved')
    setTimeout(() => { saveStatus.value = null }, 2000)
  } catch (err) {
    const msg = err?.response?.data?.error || err?.message || 'Failed to save unit settings'
    errorMsg.value = msg
    toast.error(msg)
  } finally {
    isSaving.value = false
  }
}

const handleDiscard = () => {
  formData.enabled_unit_codes = [...originalData.value.enabled_unit_codes]
  formData.default_unit_code = originalData.value.default_unit_code
  errorMsg.value = ''
}

const fetchSettings = async () => {
  isLoading.value = true
  try {
    const settings = await getBillingSettings()
    const codes = settings?.enabled_unit_codes
    formData.enabled_unit_codes = codes && codes.length > 0
      ? [...codes]
      : ['PCS', 'KGS', 'GMS', 'LTR', 'MLT', 'MTR', 'BOX', 'PAC', 'SET', 'NOS', 'DOZ', 'BDL', 'ROL', 'PRS', 'BAG', 'BTL', 'CTN', 'TON', 'QTL', 'OTH']
    formData.default_unit_code = settings?.default_unit_code || 'PCS'
    originalData.value = {
      enabled_unit_codes: [...formData.enabled_unit_codes],
      default_unit_code: formData.default_unit_code,
    }
  } catch {
    toast.error('Failed to load unit settings')
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchSettings()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.settings-section {
  padding: var(--space-6);
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-1) 0;
}

.section-description {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-6) 0;
}

.loading-state {
  padding: var(--space-8);
  text-align: center;
  font-size: var(--font-size-sm);
  color: var(--color-gray-400);
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: 0 0 var(--space-3) 0;
}

.error-banner {
  padding: var(--space-3) var(--space-4);
  background: var(--color-red-50, #fef2f2);
  border: 1px solid var(--color-red-200, #fecaca);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-red, #dc2626);
  margin-bottom: var(--space-3);
}

/* Unit groups */
.unit-groups {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.unit-group {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.unit-group-header {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-gray-500);
  background: var(--color-gray-50);
  border-bottom: 1px solid var(--color-gray-200);
}

.unit-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0;
}

.unit-checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  cursor: pointer;
  border-bottom: 1px solid var(--color-gray-100);
  border-right: 1px solid var(--color-gray-100);
  transition: background var(--transition-fast);
}

.unit-checkbox-label:hover {
  background: var(--color-gray-50);
}

.unit-checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: var(--color-blue);
  cursor: pointer;
}

.unit-code {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  min-width: 36px;
  font-family: monospace;
}

.unit-name {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Save Footer */
.save-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-6);
  margin: var(--space-6) calc(-1 * var(--space-6)) calc(-1 * var(--space-6));
  background: var(--color-white);
  border-top: 1px solid var(--color-gray-200);
}

.btn-discard {
  padding: var(--space-2) var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  background: transparent;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-discard:hover {
  background: var(--color-gray-50);
  border-color: var(--color-gray-400);
}
</style>
